import React, { useState } from 'react';
import axios from 'axios';
import QuestionDisplay from './QuestionDisplay';
import PostDisplay from './PostDisplay';
import WorkedExampleDisplay from './WorkedExampleDisplay';
import './SavedContent.css';

const SavedContent = ({ userId, savedItems, onUpdate }) => {
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [editingNotes, setEditingNotes] = useState(null);

  const filteredItems = savedItems.filter(item => {
    const matchesType = filterType === 'all' || item.type === filterType;
    const matchesSearch = !searchTerm || 
      (item.notes && item.notes.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.tags && item.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())));
    return matchesType && matchesSearch;
  });

  const handleRemove = async (contentId) => {
    try {
      await axios.delete(
        `/api/metrics/saved/${userId}/${contentId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      onUpdate();
    } catch (err) {
      console.error('Error removing saved content:', err);
    }
  };

  const handleBulkRemove = async () => {
    if (selectedItems.length === 0) return;
    
    if (window.confirm(`Remove ${selectedItems.length} items?`)) {
      for (const itemId of selectedItems) {
        await handleRemove(itemId);
      }
      setSelectedItems([]);
    }
  };

  const handleUpdateNotes = async (item, newNotes) => {
    try {
      await axios.post(
        '/api/metrics/save',
        {
          userId,
          contentId: item.contentId?._id || item.contentId,
          type: item.type,
          notes: newNotes,
          tags: item.tags
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      setEditingNotes(null);
      onUpdate();
    } catch (err) {
      console.error('Error updating notes:', err);
    }
  };

  const renderSavedItem = (item) => {
    const isSelected = selectedItems.includes(item._id);
    
    return (
      <div 
        key={item._id}
        className={`saved-item ${viewMode} ${isSelected ? 'selected' : ''}`}
      >
        <div className="saved-item-header">
          <input 
            type="checkbox"
            checked={isSelected}
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedItems([...selectedItems, item._id]);
              } else {
                setSelectedItems(selectedItems.filter(id => id !== item._id));
              }
            }}
          />
          <span className="saved-type-badge">{item.type}</span>
          <span className="saved-date">
            {new Date(item.savedAt).toLocaleDateString()}
          </span>
          <button 
            className="remove-btn"
            onClick={() => handleRemove(item.contentId?._id || item.contentId)}
          >
            ✕
          </button>
        </div>
        
        <div className="saved-item-content">
          {/* Content display based on type - simplified for now */}
          <p>Content ID: {item.contentId?._id || item.contentId}</p>
        </div>
        
        <div className="saved-item-meta">
          {item.tags && item.tags.length > 0 && (
            <div className="saved-tags">
              {item.tags.map((tag, index) => (
                <span key={index} className="tag">#{tag}</span>
              ))}
            </div>
          )}
          
          <div className="saved-notes">
            {editingNotes === item._id ? (
              <div className="notes-editor">
                <textarea 
                  defaultValue={item.notes}
                  onBlur={(e) => handleUpdateNotes(item, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.ctrlKey) {
                      handleUpdateNotes(item, e.target.value);
                    }
                  }}
                  placeholder="Add notes..."
                  autoFocus
                />
                <button onClick={() => setEditingNotes(null)}>Cancel</button>
              </div>
            ) : (
              <div 
                className="notes-display"
                onClick={() => setEditingNotes(item._id)}
              >
                {item.notes || <span className="placeholder">Click to add notes...</span>}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="saved-content">
      <div className="saved-header">
        <h3>Saved Content ({filteredItems.length})</h3>
        <div className="saved-controls">
          <div className="filter-group">
            <button 
              className={filterType === 'all' ? 'active' : ''}
              onClick={() => setFilterType('all')}
            >
              All
            </button>
            <button 
              className={filterType === 'question' ? 'active' : ''}
              onClick={() => setFilterType('question')}
            >
              Questions
            </button>
            <button 
              className={filterType === 'post' ? 'active' : ''}
              onClick={() => setFilterType('post')}
            >
              Posts
            </button>
            <button 
              className={filterType === 'workedExample' ? 'active' : ''}
              onClick={() => setFilterType('workedExample')}
            >
              Examples
            </button>
          </div>
          
          <input 
            type="text"
            className="search-input"
            placeholder="Search by tags or notes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          
          {selectedItems.length > 0 && (
            <button 
              className="bulk-remove-btn"
              onClick={handleBulkRemove}
            >
              Remove {selectedItems.length} items
            </button>
          )}
        </div>
      </div>
      
      <div className={`saved-items-container ${viewMode}`}>
        {filteredItems.length > 0 ? (
          filteredItems.map(renderSavedItem)
        ) : (
          <div className="empty-state">
            <p>No saved content found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SavedContent;